import React from "react";
import { FileText, Rocket, Check } from "lucide-react";

// How hard Autopilot reworks the résumé for each job. One component, used in onboarding and in
// /apply/settings, so the two can never drift.
//
// The default is "tailored" because that is what the system already did before this setting existed —
// switching it on changes nothing for anyone until they choose otherwise.
export const RESUME_MODES = [
  {
    id: "original",
    icon: FileText,
    title: "Original",
    blurb: "Sent exactly as you wrote it. No changes.",
    detail: "Your file, your layout — and the fastest option.",
  },
  {
    id: "tailored",
    icon: Rocket,
    title: "Tailored",
    recommended: true,
    blurb: "Rewritten for each job, from your real experience.",
    // Generic by design: an example drawn from one candidate's résumé means nothing to the next, and
    // any claim about HOW the rewrite restructures goes stale when the prompt changes — this card
    // once promised "the most relevant role first", which was false the day reordering was removed.
    detail: "Your roles, dates and education never change.",
  },
];

export default function ResumeModePicker({ value, onChange, note }) {
  const current = RESUME_MODES.some((m) => m.id === value) ? value : "tailored";
  return (
    <div>
      <div className="grid grid-cols-1 gap-2">
        {RESUME_MODES.map((m) => {
          const Icon = m.icon;
          const on = current === m.id;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => onChange(m.id)}
              aria-pressed={on}
              className={`text-left rounded-xl border p-3 transition ${
                on ? "border-[#0025D8] bg-[#0025D8]/[0.04] ring-1 ring-[#0025D8]/20"
                   : "border-border bg-white hover:bg-secondary/50"}`}
            >
              <div className="flex items-start gap-3">
                <span className={`mt-0.5 w-8 h-8 shrink-0 rounded-lg flex items-center justify-center ${
                  on ? "bg-[#0025D8] text-white" : "bg-secondary text-muted-foreground"}`}>
                  <Icon className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-semibold text-[#0F172A]">{m.title}</span>
                    {m.recommended && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#0025D8]/10 text-[#0025D8]">
                        Recommended
                      </span>
                    )}
                    {on && <Check className="w-3.5 h-3.5 text-[#0025D8] ml-auto shrink-0" />}
                  </div>
                  <p className="text-[12px] text-[#0F172A]/80 mt-0.5">{m.blurb}</p>
                  {on && <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">{m.detail}</p>}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {note !== false && (
        <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
          Applies to new applications; drafts already prepared are unchanged.
        </p>
      )}
    </div>
  );
}
